/* eslint-disable @typescript-eslint/no-unused-vars */
import { ActionIcon, Alert, Badge, Button, Divider, Flex, Group, Loader, Paper, Radio, Select, Stack, Switch, Text, Tooltip } from "@mantine/core";
import { useIntegrationApp } from "@membranehq/react";
import { IconListDetails, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, memo, useCallback } from "react";
import { useSyncObjectFields } from "../../../../../hooks/QueryHooks";
import type { HubSpotField } from "../../../../../services/syncConfiguration/syncConfigurationEndpoints.interface";
import useWorkspaceStore from "../../../../../store/workspace/workspaceStore";
import { Checkbox as DsCheckbox } from "../../../../../components/ui/checkbox";
import FieldMappingTable from "../components/FieldMappingTable";
// import FilterConfiguration from "../components/FilterConfiguration"; // Commented out as not properly used
import RelationshipMappingTable from "../components/RelationshipMappingTable";
import RoleMappingTable from "../components/RoleMappingTable";
import WriteBackConfiguration from "../components/WriteBackConfiguration";
import type { ConfigByObject, DraftMappingsByObject, MatchingMethod, RelationshipMappingsByObject, RoleMappingsByObject, SelectedObjects, SyncFilter, WritebackByObject } from "../types";
import { ArrowUp, Pause, Play } from "lucide-react";
import { normalizeSyncDirection } from "../../../shared/mapping-studio/adapters";

export interface ObjectPanelProps {
  slug: string;
  label?: string;
  isConnected: boolean;
  selected: SelectedObjects;
  config: ConfigByObject;
  mappings: DraftMappingsByObject;
  writeback: WritebackByObject;
  roleMappings: RoleMappingsByObject;
  relationshipMappings: RelationshipMappingsByObject;
  connectionId?: string;
  syncId?: string;
  writebackSyncId?: string; // Add writeback sync ID prop
  syncStatus?: 'enabled' | 'disabled' | 'error';
  writebackSyncStatus?: 'enabled' | 'disabled' | 'error'; // Add writeback sync status
  workspaceObjectId?: string;
  isActivating?: boolean;
  isWritebackActivating?: boolean;
  isDeletingSync?: boolean;
  isDeletingWritebackSync?: boolean;
  onToggleSelected: (slug: string, enabled: boolean) => void;
  onConfigPatch: (slug: string, patch: Partial<ConfigByObject[string]>) => void;
  onMappingsChange: (updater: (prev: DraftMappingsByObject) => DraftMappingsByObject) => void;
  onRoleMappingsChange: (updater: (prev: RoleMappingsByObject) => RoleMappingsByObject) => void;
  onRelationshipMappingsChange: (updater: (prev: RelationshipMappingsByObject) => RelationshipMappingsByObject) => void;
  onToggleWriteback: (slug: string, enabled: boolean) => void;
  onWritebackChange?: (updater: (prev: WritebackByObject) => WritebackByObject) => void;
  onCreateSync?: (slug: string, sourceCollection?: string) => Promise<string | undefined>;
  onActivateSync?: (slug: string) => Promise<void>;
  onDeactivateSync?: (slug: string) => Promise<void>;
  onDeleteSync?: (slug: string) => Promise<void> | void;
  onViewDetails?: (slug: string) => void;
  onCreateWritebackSync?: (slug: string) => Promise<void>;
  onActivateWritebackSync?: (slug: string) => Promise<void>;
  onDeactivateWritebackSync?: (slug: string) => Promise<void>;
  onDeleteWritebackSync?: (slug: string) => Promise<void> | void;
  onViewWritebackDetails?: (slug: string) => void;
  hubspotSyncConfigurations?: any[];
  // Provider-agnostic props (optional, default to HubSpot)
  providerKey?: string;
  providerLabel?: string;
  capabilities?: {
    supportsFieldMapping?: boolean;
    supportsRoleMapping?: boolean;
    supportsRelationshipMapping?: boolean;
    supportsWriteback?: boolean;
  };
}

const LABELS: Record<string, string> = {
  contacts: "Contacts",
  accounts: "Accounts",
  companies: "Companies",
};

function ObjectPanel({
  slug,
  label,
  isConnected,
  selected,
  config,
  mappings,
  writeback,
  roleMappings,
  relationshipMappings,
  connectionId,
  syncId,
  writebackSyncId,
  syncStatus,
  writebackSyncStatus,
  workspaceObjectId,
  isActivating = false,
  isWritebackActivating = false,
  isDeletingSync = false,
  isDeletingWritebackSync = false,
  onToggleSelected,
  onConfigPatch,
  onMappingsChange,
  onRoleMappingsChange,
  onRelationshipMappingsChange,
  onToggleWriteback,
  onWritebackChange,
  onCreateSync,
  onActivateSync,
  onDeactivateSync,
  onDeleteSync,
  onViewDetails,
  onCreateWritebackSync,
  onActivateWritebackSync,
  onDeactivateWritebackSync,
  onDeleteWritebackSync,
  onViewWritebackDetails,
  hubspotSyncConfigurations,
  providerKey = 'hubspot',
  providerLabel = 'HubSpot',
  capabilities,
}: ObjectPanelProps) {
  const resolvedLabel = label || LABELS[slug] || slug;
  const workspace: any = useWorkspaceStore((s: any) => s.workspace);
  const integrationApp = useIntegrationApp();

  // Get the actual provider collection name for display
  const getHubSpotCollectionName = (syncDirection: 'write_in' | 'write_out'): string => {
    if (!hubspotSyncConfigurations) return resolvedLabel;

    const canon = (x: string) => (x === "users" ? "contacts" : x);
    const sync = hubspotSyncConfigurations.find((s: any) => normalizeSyncDirection(s.sync_direction) === syncDirection && canon(s.destination_object) === slug);

    // For now, return the source_object which is the HubSpot collection
    // We could enhance this later to get the human-readable name
    if (sync?.source_object && sync.source_object !== slug) {
      // Capitalize first letter for better display
      return sync.source_object.charAt(0).toUpperCase() + sync.source_object.slice(1);
    }

    return resolvedLabel;
  };

  // State for provider collections and fields
  const [hubspotCollections, setHubspotCollections] = useState<Array<{ key: string, name: string, fields: HubSpotField[] }>>([]);
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);
  const [hubspotFields, setHubspotFields] = useState<HubSpotField[]>([]);
  const [loadedCollections, setLoadedCollections] = useState<Set<string>>(new Set());
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [existingMappingIds, setExistingMappingIds] = useState<Record<string, string>>({});
  const [creatingImport, setCreatingImport] = useState<boolean>(false);

  const updateConfig = useCallback((patch: Partial<ConfigByObject[string]>) => {
    onConfigPatch(slug, patch);
  }, [onConfigPatch, slug]);

  // Check if field mappings are properly configured for sync activation
  const hasValidFieldMappings = (): boolean => {
    const currentMappings = mappings[slug] || [];
    // A sync is valid if it has at least one field mapping configured (besides matcher field)
    const validMappings = currentMappings.filter(mapping =>
      mapping.source_field_name &&
      mapping.destination_field_id &&
      mapping.mapping_status === 'mapped'
    );
    return validMappings.length > 0;
  };

  // Check if writeback field mappings are configured
  const hasValidWritebackFieldMappings = (): boolean => true; // TODO: Implement proper validation

  // Check if sync can be activated (requires both matcher field and field mappings)
  const canActivateSync = (): boolean => {
    const hasMatcherField = Boolean(config[slug]?.matcher_field_name);
    const hasFieldMappings = hasValidFieldMappings();
    return hasMatcherField && hasFieldMappings;
  };

  // Check if writeback sync can be activated (requires field mappings)
  const canActivateWritebackSync = (): boolean => hasValidWritebackFieldMappings();

  // Get HubSpot fields that are already used to exclude from role mapping selectors
  // NOTE: Role mappings can reuse fields among themselves (CSM, Owner, AE can all use same field)
  const getUsedHubSpotFields = (): Set<string> => {
    const usedFields = new Set<string>();

    // Add fields from current field mappings (regular field mappings only)
    const currentMappings = mappings[slug] || [];
    currentMappings.forEach(mapping => {
      if (mapping.source_field_name) {
        usedFields.add(mapping.source_field_name);
      }
    });

    // Add matcher field
    if (config[slug]?.matcher_field_name) {
      usedFields.add(config[slug].matcher_field_name as any);
    }

    // NOTE: We do NOT add role mapping fields to usedFields because:
    // - Multiple roles can map to the same HubSpot field (e.g., all roles use "hubspot_owner_id")
    // - Role mappings should only exclude regular field mappings and matcher field

    return usedFields;
  };

  // Get workspace fields for current object using the passed workspaceObjectId
  // Map frontend slug to backend API slug (contacts -> users)
  const apiSlug = slug === 'contacts' ? 'users' : slug;
  const { data: workspaceFields } = useSyncObjectFields({
    workspaceId: workspace?.id,
    objectSlug: apiSlug,
    enabled: !!workspace?.id && !!workspaceObjectId
  });

  const queryClient = useQueryClient();

  // Function to refresh workspace fields
  const refreshWorkspaceFields = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ["sync-object-fields", workspace?.id, apiSlug]
    });
  }, [apiSlug, queryClient, workspace?.id]);

  // Fetch provider collections list (without fields)
  const fetchHubSpotCollections = async () => {
    if (!isConnected) return;
    setCollectionsLoading(true);
    try {
      const collections = await integrationApp
        .integration(providerKey)
        .getDataCollections();

      // Only store collection info without fields initially
      const collectionsWithoutFields = collections.map((col: any) => ({
        key: col.key,
        name: col.name,
        fields: [] // Start with empty fields
      }));

      setHubspotCollections(collectionsWithoutFields);
    } catch (error) {
      console.error(`Failed to fetch ${providerLabel} collections:`, error);
    } finally {
      setCollectionsLoading(false);
    }
  };

  // Track provider field schema loading
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // Fetch fields for a specific collection
  const fetchCollectionFields = async (collectionKey: string): Promise<HubSpotField[]> => {
    if (!isConnected || !connectionId) return [];

    try {
      setFieldsLoading(true);
      const collection = await integrationApp.connection(connectionId).dataCollection(collectionKey).get();

      if (collection?.fieldsSchema?.properties) {
        const fields: HubSpotField[] = Object.entries(collection.fieldsSchema.properties).map(([key, field]: [string, any]) => ({
          ...field,
          key,
          title: field.title || field.displayName || key,
          type: field.type || 'string',
          readOnly: field.readOnly || false,
          required: field.required || false,
        }));
        // Update the collections state with the fetched fields (insert if missing)
        setHubspotCollections(prev => {
          const exists = prev.some(col => col.key === collectionKey);
          if (exists) {
            return prev.map(col => (col.key === collectionKey ? { ...col, fields } : col));
          }
          return [...prev, { key: collectionKey, name: collectionKey, fields }];
        });
        // Mark as loaded and set fields immediately
        setLoadedCollections(prev => {
          const next = new Set(prev);
          next.add(collectionKey);
          return next;
        });
        setHubspotFields(fields);
        return fields;
      }
      return [];
    } catch (error) {
      console.error(`Failed to fetch fields for collection ${collectionKey}:`, error);
      return [];
    } finally {
      setFieldsLoading(false);
    }
  };

  // Get current dataSource collection for this object
  const getCurrentDataSourceCollection = async (): Promise<string | null> => {
    if (!connectionId) return null;

    try {
      const dataSource = await integrationApp
        .connection(connectionId)
        .dataSource(slug)
        .get();

      return dataSource?.collectionKey || null;
    } catch (error) {
      console.error(`Failed to get dataSource for ${slug}:`, error);
      return null;
    }
  };

  // Fetch provider collections and get current dataSource on mount when connected
  useEffect(() => {
    const initializeCollections = async () => {
      if (!isConnected || !integrationApp) return;

      // First fetch the collections list
      await fetchHubSpotCollections();

      // Then get the current dataSource collection for this object
      const currentCollection = await getCurrentDataSourceCollection();
      if (currentCollection) {
        setSelectedCollection(currentCollection);
        // Fetch fields for the current collection
        await fetchCollectionFields(currentCollection);
      }
    };

    initializeCollections().catch(console.error);
  }, [isConnected, integrationApp, connectionId, slug]);

  // Update fields when collection changes
  useEffect(() => {
    if (selectedCollection) {
      const collection = hubspotCollections.find(c => c.key === selectedCollection);
      setHubspotFields(collection?.fields || []);
      if (!loadedCollections.has(selectedCollection)) {
        // If collection fields are not yet loaded, fetch them now
        fetchCollectionFields(selectedCollection).catch(console.error);
      }
    }
  }, [selectedCollection, hubspotCollections, loadedCollections]);

  // Fallback selected collection from config if connection's data source has none
  useEffect(() => {
    if (!isConnected) return;
    if (!selectedCollection && config[slug]?.source_collection) {
      const key = config[slug]?.source_collection as string;
      setSelectedCollection(key);
      // Ensure fields are fetched for the configured collection
      fetchCollectionFields(key).catch(console.error);
    }
  }, [isConnected, selectedCollection, config[slug]?.source_collection]);

  // Reconcile matcher defaults when fields load or method changes
  useEffect(() => {
    const cfg = config[slug];
    if (!cfg || !cfg.matching_method) return;
    const patch: any = {};

    if (cfg.matching_method === 'external_id') {
      if (!cfg.hyperengage_matcher_field) {
        patch.hyperengage_matcher_field = 'external_id';
      }
      const exists = cfg.matcher_field_name && hubspotFields.some(f => f.key === cfg.matcher_field_name);
      if (!exists) {
        const idField = hubspotFields.find(f => /(^id$|_id$|^hs_object_id$|object.?id$)/i.test(f.key) || /\bid\b/i.test(f.title || ''));
        if (idField) patch.matcher_field_name = idField.key;
      }
    } else if (cfg.matching_method === 'custom_field') {
      if (!cfg.hyperengage_matcher_field) {
        patch.hyperengage_matcher_field = 'external_id';
      }
      if (cfg.matcher_field_name && !hubspotFields.some(f => f.key === cfg.matcher_field_name)) {
        // Clear invalid provider matcher to force user to re-select from the current collection
        patch.matcher_field_name = undefined;
      }
    }

    if (Object.keys(patch).length) {
      updateConfig(patch);
    }
  }, [hubspotFields, config[slug]?.matching_method]);

  // Derived readiness for UI that depends on provider fields
  const fieldsReady = Boolean(selectedCollection) && !fieldsLoading;

  // Fetch fields when writeback is enabled but fields are empty
  useEffect(() => {
    const shouldFetchFields = writeback[slug]?.enabled &&
      selectedCollection &&
      hubspotFields.length === 0 &&
      !collectionsLoading;

    if (shouldFetchFields) {
      fetchCollectionFields(selectedCollection).catch(console.error);
    }
  }, [writeback, slug, selectedCollection, hubspotFields.length, collectionsLoading, fetchCollectionFields]);

  // Handle collection selection with integration.app dataSource updates
  const handleCollectionChange = async (collectionKey: string | null) => {
    if (!collectionKey) return;

    try {
      setCollectionsLoading(true);

      // Update data source in integration.app to match selected collection
      if (connectionId) {
        await integrationApp
          .connection(connectionId)
          .dataSource(slug)
          .patch({
            collectionKey,
            collectionParameters: {},
          });
      }

      // Update local state
      setSelectedCollection(collectionKey);
      updateConfig({ source_collection: collectionKey });

      // Fetch fields for the selected collection
      await fetchCollectionFields(collectionKey);
    } catch (error) {
      console.error('Failed to update collection:', error);
    } finally {
      setCollectionsLoading(false);
    }
  };

  // Note: Default collection is now set from dataSource in the initialization effect

  return (
    <Paper p="md" className="border">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>{resolvedLabel}</Text>
          {/* Only show button if no sync exists */}
          {!syncId && (
            <Button
              variant={selected[slug] ? "light" : "filled"}
              color={selected[slug] ? "gray" : "blue"}
              size="sm"
              loading={creatingImport}
              disabled={creatingImport}
              onClick={() => {
                if (creatingImport) return;
                const willSelect = !selected[slug];
                if (!willSelect) {
                  onToggleSelected(slug, willSelect);
                  return;
                }

                const collectionKey = selectedCollection ?? config[slug]?.source_collection;
                if (!syncId && isConnected && onCreateSync && collectionKey) {
                  setCreatingImport(true);
                  Promise.resolve(onCreateSync(slug, collectionKey))
                    .then(() => {
                      onToggleSelected(slug, true);
                      // Refresh workspace fields so system fields appear immediately
                      refreshWorkspaceFields();
                    })
                    .catch(console.error)
                    .finally(() => setCreatingImport(false));
                  return;
                }

                onToggleSelected(slug, willSelect);
              }}
            >
              {selected[slug] ? "Cancel Setup" : "Configure Import"}
            </Button>
          )}
        </Group>

        {selected[slug] && (
          <>
            {/* Sync Summary Cards - shown when syncs exist */}
            {(syncId || writebackSyncId) && (

              <Stack gap="xs">
                <Divider my="xs" label="Sync Configuration" labelPosition="left" />
                {/* Writeback Sync Activation Alert */}
                {writebackSyncId && writeback[slug]?.enabled === false && (
                  <Alert color="purple" p="md">
                    <Group justify="space-between" align="center">
                      <div>
                        <Text fw={500} size="sm" mb={4}>
                          🔄 Enable Writeback Sync
                        </Text>
                        <Text size="xs" c="dimmed">
                          {hasValidWritebackFieldMappings()
                            ? 'Your writeback sync is configured and ready to be enabled'
                            : 'Configure at least one field mapping in the Write-back section below'}
                        </Text>
                      </div>
                      <Switch
                        size="md"
                        color="purple"
                        checked={Boolean(writeback[slug]?.enabled)}
                        onChange={(e) => onToggleWriteback(slug, e.currentTarget.checked)}
                        disabled={!isConnected || !hasValidWritebackFieldMappings()}
                      />
                    </Group>
                    {!hasValidWritebackFieldMappings() && (
                      <Text size="xs" c="red" mt={8} fw={500}>
                        ⚠️ At least one field mapping is required to enable writeback sync
                      </Text>
                    )}
                  </Alert>
                )}

                {!syncId ? (
                  <Group justify="space-between">
                    <div>
                      <Text size="sm">Create import sync from {providerLabel}</Text>
                      <Text size="xs" c="dimmed">
                        Set up inbound sync to bring {resolvedLabel.toLowerCase()} from {providerLabel}
                      </Text>
                    </div>
                    <Button
                      size="xs"
                      variant="filled"
                      color="purple"
                      loading={creatingImport}
                      disabled={!isConnected || (!selectedCollection && !config[slug]?.source_collection) || creatingImport}
                      onClick={() => {
                        if (!onCreateSync || creatingImport) return;
                        const collectionKey = selectedCollection ?? config[slug]?.source_collection;
                        if (!collectionKey) return;
                        setCreatingImport(true);
                        Promise.resolve(onCreateSync(slug, collectionKey))
                          .then(() => {
                            // Refresh workspace fields so system fields appear immediately
                            refreshWorkspaceFields();
                          })
                          .catch(console.error)
                          .finally(() => setCreatingImport(false));
                      }}
                    >
                      Create Import Sync
                    </Button>
                  </Group>
                ) : (
                  <Paper p="sm" className="border bg-purple-50" withBorder>
                    <Flex justify="between" align="center">
                      <Flex align="center" gap="sm" style={{ flex: 1 }}>
                        <Badge variant="filled" color="purple" size="xs">Import</Badge>
                        <Badge
                          variant="dot"
                          color={syncStatus === 'enabled' ? 'green' : syncStatus === 'error' ? 'red' : 'gray'}
                          size="xs"
                        >
                          {syncStatus || 'disabled'}
                        </Badge>
                        <Tooltip
                          multiline
                          maw={300}
                          label={<Stack gap="xs">
                            <Text size="xs">{providerLabel} {getHubSpotCollectionName('write_in')} → Hyperengage {resolvedLabel}</Text>
                            <Text size="xs">Sync ID: {syncId}</Text>
                            <Text size="xs">{syncStatus === 'enabled' ? `Actively importing data from ${providerLabel}` :
                              syncStatus === 'error' ? 'Import sync encountered errors - needs attention' :
                                'Import sync is paused - can be activated when configured'}
                            </Text>
                          </Stack>}
                        >
                          <Text fw={500} size="sm" style={{ cursor: 'help' }}>
                            {providerLabel} → Hyperengage
                          </Text>
                        </Tooltip>
                        {syncStatus === 'disabled' && !canActivateSync() && (
                          <Tooltip
                            label={
                              !config[slug]?.matcher_field_name && !hasValidFieldMappings()
                                ? 'Configure matcher field and field mappings to activate'
                                : !config[slug]?.matcher_field_name
                                  ? 'Configure matcher field to activate'
                                  : 'Configure field mappings to activate'
                            }
                            multiline
                          >
                            <Text size="xs" c="orange" fw={500}>⚠️ Needs configuration</Text>
                          </Tooltip>
                        )}
                        {syncStatus === 'error' && (
                          <Text size="xs" c="red" fw={500}>❌ Error</Text>
                        )}
                      </Flex>
                      <Group gap="xs">
                        {syncStatus === 'enabled' ? (
                          <Tooltip
                            label={`Pause sync to stop importing data from ${providerLabel}`}
                            multiline
                          >
                            <Button
                              variant="filled"
                              color="purple"
                              className="bg-purple-100 hover:bg-purple-300"
                              size="xs"
                              leftSection={<Pause className="fill-purple-400" size="0.75rem" />}
                              loading={isActivating}
                              onClick={() => { onDeactivateSync?.(slug).catch(console.error); }}
                            >
                              Pause
                            </Button>
                          </Tooltip>
                        ) : (
                          <Tooltip
                            label={canActivateSync() ? `Activate sync to start importing data from ${providerLabel}` : "Configure matcher field and field mappings to activate"}
                            multiline
                          >
                            <Button
                              variant="filled"
                              color="purple"
                              leftSection={<Play className="fill-purple-400" size="0.75rem" />}
                              className="bg-purple-200 hover:bg-purple-300 disabled:bg-gray-200"
                              size="xs"
                              loading={isActivating}
                              disabled={!canActivateSync()}
                              onClick={() => {
                                if (!canActivateSync()) return;
                                onActivateSync?.(slug).catch(console.error);
                              }}
                            >
                              {syncStatus === 'error' ? 'Retry' : 'Activate'}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip label="View sync details and history">
                          <ActionIcon
                            variant="subtle"
                            color="gray.5"
                            size="sm"
                            onClick={() => onViewDetails?.(slug)}
                          >
                            <IconListDetails size="0.75rem" />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete sync configuration">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            loading={isDeletingSync}
                            disabled={isDeletingSync}
                            onClick={() => { onDeleteSync?.(slug); }}
                          >
                            <IconTrash size="0.75rem" />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Flex>
                  </Paper>
                )}

                {/* Write-back Sync Card - Compact */}
                {writebackSyncId && writeback[slug]?.enabled && (
                  <Paper p="sm" className="border bg-purple-50" withBorder>
                    <Flex justify="between" align="center">
                      <Flex align="center" gap="sm" style={{ flex: 1 }}>
                        <Badge variant="filled" color="red" size="xs">Write-back</Badge>
                        <Badge
                          variant="dot"
                          color={writebackSyncStatus === 'enabled' ? 'green' : writebackSyncStatus === 'error' ? 'red' : 'gray'}
                          size="xs"
                        >
                          {writebackSyncStatus || 'disabled'}
                        </Badge>
                        <Tooltip
                          label={
                            <Stack gap="xs">
                              <Text size="xs">Hyperengage {resolvedLabel} → {providerLabel} {getHubSpotCollectionName('write_out')}</Text>
                              <Text size="xs">Sync ID: {writebackSyncId}</Text>
                              <Text size="xs">{writebackSyncStatus === 'enabled' ? `Actively writing data back to ${providerLabel}` :
                                writebackSyncStatus === 'error' ? 'Write-back sync encountered errors - needs attention' :
                                  'Write-back sync is paused - can be activated when configured'}</Text>
                            </Stack>
                          }
                          multiline
                        >
                          <Text fw={500} size="sm" style={{ cursor: 'help' }}>
                            Hyperengage → {providerLabel}
                          </Text>
                        </Tooltip>
                        {writebackSyncStatus === 'disabled' && !canActivateWritebackSync() && (
                          <Tooltip label="Configure writeback field mappings to activate" multiline>
                            <Text size="xs" c="orange" fw={500}>⚠️ Needs configuration</Text>
                          </Tooltip>
                        )}
                        {writebackSyncStatus === 'error' && (
                          <Text size="xs" c="red" fw={500}>❌ Error</Text>
                        )}
                      </Flex>
                      <Group gap="xs">
                        {writebackSyncStatus === 'enabled' ? (
                          <Tooltip label={`Pause write-back sync to stop exporting data to ${providerLabel}`}>
                            <Button
                              variant="filled"
                              color="purple"
                              leftSection={<Pause className="fill-purple-400" size="0.75rem" />}
                              className="bg-purple-100 hover:bg-purple-300"
                              size="xs"
                              loading={isWritebackActivating}
                              onClick={() => { onDeactivateWritebackSync?.(slug).catch(console.error); }}
                            >
                              Pause
                            </Button>
                          </Tooltip>

                        ) : (
                          <Tooltip label={canActivateWritebackSync() ? `Activate write-back sync to start exporting data to ${providerLabel}` : "Configure writeback field mappings to activate"}>
                            <Button
                              variant="filled"
                              color="purple"
                              className="bg-purple-200 hover:bg-purple-300 disabled:bg-gray-200"
                              size="xs"
                              leftSection={<Play size="0.75rem" className="fill-purple-400" />}
                              disabled={!canActivateWritebackSync()}
                              loading={isWritebackActivating}
                              onClick={() => { onActivateWritebackSync?.(slug).catch(console.error); }}
                            >
                              {writebackSyncStatus === 'error' ? 'Retry' : 'Activate'}
                            </Button>
                          </Tooltip>
                        )}
                        <Tooltip label="View sync details and history">
                          <ActionIcon
                            variant="subtle"
                            color="gray.5"
                            size="sm"
                            onClick={() => onViewWritebackDetails?.(slug)}
                          >
                            <IconListDetails size="0.75rem" />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete sync configuration">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            loading={isDeletingWritebackSync}
                            disabled={isDeletingWritebackSync}
                            onClick={() => { onDeleteWritebackSync?.(slug); }}
                          >
                            <IconTrash size="0.75rem" />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Flex>
                  </Paper>
                )}
              </Stack>
            )}

            {/* Provider Collection Selection */}
            <Divider my="xs" label={`${providerLabel} Collection`} labelPosition="left" />
            <Stack gap="xs">
              <Text size="sm" c="dimmed">Select which {providerLabel} object to sync with</Text>
              <Select
                placeholder={`Select ${providerLabel} collection`}
                data={hubspotCollections.map(col => ({
                  value: col.key,
                  label: col.name
                }))}
                value={selectedCollection}
                onChange={(value) => {
                  handleCollectionChange(value).catch(console.error);
                }}
                disabled={collectionsLoading}
                rightSection={collectionsLoading ? <Loader size="sm" /> : null}
              />
            </Stack>

            {/* Matching Configuration */}
            {selectedCollection && (
              <>
                <Divider my="xs" label="Record Matching" labelPosition="left" />
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">Choose how records should be matched between systems</Text>
                  <Radio.Group
                    color="purple"
                    value={config[slug]?.matching_method || "external_id"}
                    onChange={(value) => {
                      const method = value as MatchingMethod;
                      // Auto-set HyperEngage field based on matching method
                      let hyperengageField: string | undefined;
                      if (method === "external_id") {
                        hyperengageField = "external_id";
                      } else {
                        hyperengageField = undefined; // Let user choose for custom
                      }

                      updateConfig({
                        matching_method: method,
                        hyperengage_matcher_field: hyperengageField,
                        // Reset HubSpot field when method changes
                        matcher_field_name: undefined
                      });
                    }}
                  >
                    <Stack gap="md">
                      <Radio
                        color="purple"
                        value="external_id"
                        label="External ID (Recommended)"
                        description={`Use ${providerLabel}'s unique ID for matching - most reliable method`}
                      />
                      {/* Email matching removed due to error-prone behavior */}
                      {/* <Radio
                        value="email"
                        label="Email Address"
                        description="Match records by email address"
                      /> */}
                      <Radio
                        color="purple"
                        value="custom_field"
                        label="Custom Field"
                        description="Select a custom field for matching"
                      />
                    </Stack>
                  </Radio.Group>
                </Stack>

                {/* Matcher Fields */}
                {config[slug]?.matching_method === "custom_field" ? (
                  // Show both selects for custom field matching
                  <Group grow>
                    <Select
                      label={`${providerLabel} Field`}
                      placeholder={`Select ${providerLabel} field for matching`}
                      data={hubspotFields.map(field => ({
                        value: field.key,
                        label: `${field.title} (${field.type})`
                      }))}
                      value={config[slug]?.matcher_field_name || ""}
                      onChange={(value) => updateConfig({ matcher_field_name: value || undefined })}
                      description={`Field in ${providerLabel} used for matching`}
                      searchable
                    />
                    <Select
                      label="HyperEngage Field"
                      placeholder="Select HyperEngage field for matching"
                      data={(() => {
                        const fields = Array.isArray(workspaceFields) ? workspaceFields.map((field: any) => ({
                          value: field.name,
                          label: field.display_name || field.column_name
                        })) : [];
                        if (!fields.find(f => f.value === 'external_id')) {
                          fields.unshift({ value: 'external_id', label: 'External ID' });
                        }
                        if (!fields.find(f => f.value === 'email')) {
                          fields.unshift({ value: 'email', label: 'Email' });
                        }
                        return fields;
                      })()}
                      value={config[slug]?.hyperengage_matcher_field || ""}
                      onChange={(value) => updateConfig({ hyperengage_matcher_field: value || undefined })}
                      description="Field in HyperEngage used for matching"
                      searchable
                    />
                  </Group>
                ) : (
                  // Show only provider field select for external_id
                  <Group grow>
                    <Select
                      label={`${providerLabel} Field`}
                      placeholder={`Select ${providerLabel} ID field`}
                      data={hubspotFields
                        .filter(field => {
                          if (config[slug]?.matching_method === "external_id") {
                            return field.key.toLowerCase().includes('id') ||
                              field.key.toLowerCase().includes('external') ||
                              field.title?.toLowerCase().includes('id');
                          }
                          return true;
                        })
                        .map(field => ({
                          value: field.key,
                          label: `${field.title} (${field.type})`
                        }))}
                      value={config[slug]?.matcher_field_name || ""}
                      onChange={(value) => updateConfig({ matcher_field_name: value || undefined })}
                      description={`Field in ${providerLabel} containing unique IDs`}
                      searchable
                    />
                  </Group>
                )}
              </>
            )}

            {/* Record Actions */}
            {selectedCollection && (
              <>
                <Divider my="xs" label="Record Actions" labelPosition="left" />
                <Stack gap="md">
                  <Group>
                    <label className="flex cursor-pointer items-start gap-2">
                      <DsCheckbox
                      checked={config[slug]?.allow_creates ?? true}
                      onCheckedChange={(checked) => updateConfig({ allow_creates: checked === true })}
                      className="mt-0.5 h-4 w-4"
                    />
                      <span>
                        <Text size="sm" fw={500}>Create new records when no match found</Text>
                        <Text size="xs" c="dimmed">Allow creating new records in HyperEngage when no matching record exists</Text>
                      </span>
                    </label>
                  </Group>
                  <Group>
                    <label className="flex cursor-pointer items-start gap-2">
                      <DsCheckbox
                      checked={config[slug]?.allow_updates ?? true}
                      onCheckedChange={(checked) => updateConfig({ allow_updates: checked === true })}
                      className="mt-0.5 h-4 w-4"
                    />
                      <span>
                        <Text size="sm" fw={500}>Update existing records when match found</Text>
                        <Text size="xs" c="dimmed">Allow updating existing records in HyperEngage when a match is found</Text>
                      </span>
                    </label>
                  </Group>
                </Stack>
              </>
            )}

            {/* Sync Filters - Commented out as not properly used */}
            {/* {selectedCollection && (
              <>
                <Divider my="xs" label="Sync Filters" labelPosition="left" />
                <FilterConfiguration
                  filters={config[slug]?.sync_filters || []}
                  onChange={(filters) => updateConfig({ sync_filters: filters })}
                  hubspotFields={hubspotFields}
                  disabled={!isConnected}
                />
              </>
            )} */}

            {/* Conflict Resolution - Commented out as backend does not support it yet */}
            {/* {selectedCollection && (
              <>
                <Divider my="xs" label="Conflict Resolution" labelPosition="left" />
                <Stack gap="xs">
                  <Text size="sm" c="dimmed">When records conflict:</Text>
                  <Radio.Group
                    color="primary"
                    className="!gap-5"
                    value={config[slug]?.conflict_resolution || "hubspot_wins"}
                    onChange={(value) => updateConfig({ conflict_resolution: value as ConflictResolution })}
                  >
                    <Stack gap="md">
                      <Radio
                        value="hubspot_wins"
                        label="Hyperengages (recommended for import)"
                        description="Hyperengagea takes precedence"
                      />
                      <Radio
                        value="hyperengage_wins"
                        label="HyperEngage wins"
                        description="HyperEngage data takes precedence"
                      />
                      <Radio
                        value="most_recent_wins"
                        label="Most recently updated wins"
                        description="Use timestamp to determine winner"
                      />
                    </Stack>
                  </Radio.Group>
                </Stack>
              </>
            )} */}

            {/* Role Mappings */}
            {selectedCollection && (slug === 'accounts' || slug === 'companies') && (capabilities?.supportsRoleMapping ?? true) && (
              <>
                <Divider my="xs" label="Role Mapping" labelPosition="left" />
                <RoleMappingTable
                  slug={slug}
                  roleMappings={roleMappings[slug] || []}
                  onChange={(newMappings) => onRoleMappingsChange((prev) => ({ ...prev, [slug]: newMappings }))}
                  isConnected={isConnected}
                  hubspotFields={hubspotFields}
                  connectionId={connectionId}
                  usedHubSpotFields={getUsedHubSpotFields()}
                  syncId={syncId}
                  workspaceFields={Array.isArray(workspaceFields) ? workspaceFields : []}
                  existingMappingIds={existingMappingIds}
                  providerLabel={providerLabel}
                  providerKey={providerKey}
                />
              </>
            )}

            {/* Relationship Mappings */}
            {selectedCollection && slug === 'contacts' && (capabilities?.supportsRelationshipMapping ?? true) && (
              <>
                <Divider my="xs" label="Relationship Mapping" labelPosition="left" />
                <RelationshipMappingTable
                  slug={slug}
                  relationshipMappings={relationshipMappings[slug] || []}
                  onChange={(newMappings) => onRelationshipMappingsChange((prev) => ({ ...prev, [slug]: newMappings }))}
                  isConnected={isConnected}
                  hubspotFields={hubspotFields}
                  connectionId={connectionId}
                  usedHubSpotFields={getUsedHubSpotFields()}
                  enabledObjects={Object.keys(selected).filter(objectSlug => selected[objectSlug] && objectSlug !== 'contacts')}
                  providerLabel={providerLabel}
                  providerKey={providerKey}
                />
              </>
            )}

            {/* Field Mappings */}
            {selectedCollection && (capabilities?.supportsFieldMapping ?? true) && (
              <>
                <Divider my="xs" label="Field mappings" labelPosition="left" />
                <FieldMappingTable
                  slug={slug}
                  mappings={mappings[slug] || []}
                  onChange={(newMappings) => onMappingsChange((prev) => ({ ...prev, [slug]: newMappings }))}
                  isConnected={isConnected}
                  workspaceFields={Array.isArray(workspaceFields) ? workspaceFields : []}
                  hubspotFields={hubspotFields}
                  workspaceObjectId={workspaceObjectId}
                  syncId={syncId}
                  connectionId={connectionId}
                  roleMappings={roleMappings[slug] || []}
                  matcherField={config[slug]?.matcher_field_name && config[slug]?.hyperengage_matcher_field ? {
                    hubspot: config[slug].matcher_field_name as string,
                    hyperengage: config[slug].hyperengage_matcher_field as string
                  } : undefined}
                  onRefreshFields={() => {
                    if (selectedCollection) {
                      fetchCollectionFields(selectedCollection).catch(console.error);
                    }
                    // Also refresh workspace fields to pick up newly created fields
                    refreshWorkspaceFields();
                  }}
                  onRoleMappingsExtracted={(extractedRoleMappings) => {
                    // Update role mappings state when extracted from database
                    onRoleMappingsChange((prev) => ({ ...prev, [slug]: extractedRoleMappings }));
                  }}
                  onRelationshipMappingsExtracted={(extractedRelationshipMappings) => {
                    // Update relationship mappings state when extracted from database
                    onRelationshipMappingsChange((prev) => ({ ...prev, [slug]: extractedRelationshipMappings }));
                  }}
                  onExistingMappingIdsExtracted={(mappingIds) => {
                    // Update existing mapping IDs state for use by role mappings
                    setExistingMappingIds(mappingIds);
                  }}
                  providerLabel={providerLabel}
                  providerKey={providerKey}
                />
              </>
            )}

            {/* Write-back Configuration */}
            {selectedCollection && (capabilities?.supportsWriteback ?? true) && (
              <>
                <Divider my="xs" label="Write-back" labelPosition="left" />
                {!writebackSyncId ? (
                  <Group justify="space-between">
                    <div>
                      <Text size="sm">Create write-back sync to {providerLabel}</Text>
                      <Text size="xs" c="dimmed">
                        Sync HyperEngage data back to {providerLabel}
                      </Text>
                    </div>
                    <Button
                      size="sm"
                      variant="filled"
                      color="purple"
                      onClick={() => { onCreateWritebackSync?.(slug).catch(console.error); }}
                      disabled={!isConnected || !syncId}
                    >
                      Create Writeback Sync
                    </Button>
                  </Group>
                ) : (
                  <Group justify="space-between">
                    <div>
                      <Text size="sm">Write-back sync to {providerLabel}</Text>
                      <Text size="xs" c="dimmed">
                        Sync HyperEngage data back to {providerLabel}
                      </Text>
                    </div>
                  </Group>
                )}

                {writeback[slug]?.enabled && writebackSyncId && (
                  <WriteBackConfiguration
                    writebackConfig={writeback[slug] || { enabled: true, fields: [], allow_creates: true, allow_updates: true }}
                    onChange={(writebackConfig) => {
                      // Update the entire writeback configuration including fields
                      if (onWritebackChange) {
                        onWritebackChange((prev) => ({
                          ...prev,
                          [slug]: writebackConfig
                        }));
                      }
                    }}
                    workspaceFields={Array.isArray(workspaceFields) ? workspaceFields : []}
                    hubspotFields={hubspotFields}
                    connectionId={connectionId}
                    syncId={writebackSyncId} // Pass writeback sync ID, not inbound
                    slug={slug}
                    onRefreshFields={() => {
                      if (selectedCollection) {
                        fetchCollectionFields(selectedCollection).catch(console.error);
                      }
                      // Also refresh workspace fields to pick up newly created fields
                      refreshWorkspaceFields();
                    }}
                    providerLabel={providerLabel}
                  />
                )}
              </>
            )}

            {/* Sync Management */}
            <Divider my="xs" label="Sync Completion" labelPosition="left" />
            <Flex justify="end">
              <Button
                size="xs"
                variant="light"
                leftSection={<ArrowUp className="w-4 h-4" />}
                onClick={() => {
                  document.querySelector('.border.bg-purple-50')?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                  });
                }}
              >
                Back to Controls
              </Button>
            </Flex>
          </>
        )}
      </Stack>
    </Paper>
  );
}

export default memo(ObjectPanel);
